/**
 * React Query hooks for API data fetching
 * Provides caching, automatic refetching, and loading/error states
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '../services/api'
import { getDisplayLabelOverride } from '../lib/constants'
import type {
  MunicipalitySearchResponse,
  Municipality,
  MunicipalitySnapshot,
  County,
  CountySnapshot,
  StateSnapshot,
  LagepromptResponse,
  MakrolageData,
  StateData,
  FederalData,
} from '../types/api'
import type { TextIndicatorListResponse } from '../services/api'

// =============================================================================
// Query Keys - centralized for consistency and cache invalidation
// =============================================================================

export const queryKeys = {
  municipalities: {
    search: (query: string) => ['municipalities', 'search', query] as const,
    detail: (key: string) => ['municipality', key] as const,
    lageprompt: (key: string) => ['municipality', key, 'lageprompt'] as const,
    editions: (key: string) => ['municipality', key, 'editions'] as const,
    snapshot: (key: string, edition: number) => ['municipality', key, 'snapshot', edition] as const,
    snapshots: (key: string) => ['municipality', key, 'snapshots'] as const,
  },
  counties: {
    detail: (key: string) => ['county', key] as const,
    makrolage: (id: number) => ['county', id, 'makrolage'] as const,
    editions: (key: string) => ['county', key, 'editions'] as const,
    snapshot: (key: string, edition: number) => ['county', key, 'snapshot', edition] as const,
  },
  states: {
    data: (id: number) => ['state', id, 'data'] as const,
    editions: (code: string) => ['state', code, 'editions'] as const,
    snapshot: (code: string, edition: number) => ['state', code, 'snapshot', edition] as const,
  },
  federal: {
    data: (year?: number) => ['federal', 'data', year] as const,
  },
  indicators: {
    entity: (entityType: string, ags: string, year?: number, maxYear?: number) => ['indicators', entityType, ags, year, maxYear] as const,
    available: (entityType: string) => ['indicators', 'available', entityType] as const,
  },
}

// =============================================================================
// Municipality Queries
// =============================================================================

/**
 * Search municipalities by name
 */
export function useSearchMunicipalities(query: string, limit = 50, offset = 0) {
  return useQuery<MunicipalitySearchResponse>({
    queryKey: queryKeys.municipalities.search(query),
    queryFn: () => api.searchMunicipalities(query || undefined, limit, offset),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get municipality details by key (AGS)
 */
export function useMunicipality(municipalityKey: string | undefined) {
  return useQuery<Municipality>({
    queryKey: queryKeys.municipalities.detail(municipalityKey!),
    queryFn: () => api.getMunicipalityByKey(municipalityKey!),
    enabled: !!municipalityKey,
  })
}

/**
 * Get Lageprompt (Lagedaten-Block + fertiger KI-Prompt) by AGS
 */
export function useMunicipalityLageprompt(municipalityKey: string | undefined) {
  return useQuery<LagepromptResponse>({
    queryKey: queryKeys.municipalities.lageprompt(municipalityKey!),
    queryFn: () => api.getMunicipalityLageprompt(municipalityKey!),
    enabled: !!municipalityKey,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get available editions for municipality
 */
export function useMunicipalityAvailableEditions(municipalityKey: string | undefined) {
  return useQuery<number[]>({
    queryKey: queryKeys.municipalities.editions(municipalityKey!),
    queryFn: () => api.getMunicipalityAvailableEditions(municipalityKey!),
    enabled: !!municipalityKey,
  })
}

/**
 * Get municipality snapshot for a specific edition
 */
export function useMunicipalitySnapshot(municipalityKey: string | undefined, edition: number | null) {
  return useQuery<MunicipalitySnapshot>({
    queryKey: queryKeys.municipalities.snapshot(municipalityKey!, edition!),
    queryFn: () => api.getMunicipalitySnapshot(municipalityKey!, edition!),
    enabled: !!municipalityKey && edition !== null,
  })
}

// =============================================================================
// County Queries
// =============================================================================

/**
 * Get county details by key (includes BBSR mappings)
 */
export function useCounty(countyKey: string | undefined) {
  return useQuery<County>({
    queryKey: queryKeys.counties.detail(countyKey!),
    queryFn: () => api.getCountyByKey(countyKey!),
    enabled: !!countyKey,
  })
}

/**
 * Get county makrolage (macro-location) data
 */
export function useCountyMakrolage(countyId: number | undefined) {
  return useQuery<MakrolageData>({
    queryKey: queryKeys.counties.makrolage(countyId!),
    queryFn: () => api.getCountyMakrolage(countyId!),
    enabled: !!countyId,
  })
}

/**
 * Get county snapshot for a specific edition
 */
export function useCountySnapshot(countyKey: string | undefined, edition: number | null) {
  return useQuery<CountySnapshot>({
    queryKey: queryKeys.counties.snapshot(countyKey!, edition!),
    queryFn: () => api.getCountySnapshot(countyKey!, edition!),
    enabled: !!countyKey && edition !== null,
  })
}

// =============================================================================
// State Queries
// =============================================================================

/**
 * Get state-level data
 */
export function useStateData(stateId: number | undefined) {
  return useQuery<StateData>({
    queryKey: queryKeys.states.data(stateId!),
    queryFn: () => api.getStateData(stateId!),
    enabled: !!stateId,
  })
}

/**
 * Get state snapshot for a specific edition
 */
export function useStateSnapshot(stateCode: string | undefined, edition: number | null) {
  return useQuery<StateSnapshot>({
    queryKey: queryKeys.states.snapshot(stateCode!, edition!),
    queryFn: () => api.getStateSnapshot(stateCode!, edition!),
    enabled: !!stateCode && edition !== null,
  })
}

// =============================================================================
// Federal Queries
// =============================================================================

/**
 * Get federal-level (Germany) data
 * @param year - Optional: specific snapshot year
 */
export function useFederalData(year?: number) {
  return useQuery<FederalData>({
    queryKey: queryKeys.federal.data(year),
    queryFn: () => api.getFederalData(year),
  })
}

// =============================================================================
// Text Indicators (Textkennzeichen) Lookup
// =============================================================================

/**
 * Get all text indicators for lookup.
 * Returns a map from code to name for easy lookup.
 */
export function useTextIndicators() {
  const query = useQuery<TextIndicatorListResponse>({
    queryKey: ['textIndicators'],
    queryFn: () => api.getTextIndicators(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - these rarely change
  })

  // Create a lookup map from code to name
  const lookup = useMemo(() => {
    const map = new Map<string, string>()
    if (query.data?.results) {
      for (const item of query.data.results) {
        map.set(item.code, item.name)
      }
    }
    return map
  }, [query.data])

  // Helper function to get display text
  const getDisplayText = (code: string | null | undefined): string => {
    if (!code) return '-'
    const name = lookup.get(code)
    return name || code
  }

  return {
    ...query,
    lookup,
    getDisplayText,
  }
}

// =============================================================================
// Field Labels for Data Sources
// =============================================================================

import type { FieldLabel } from '../services/api'

/**
 * Get field labels for a data source provider.
 * Returns the labels and helper functions for display and visibility.
 */
export function useFieldLabels(provider: string | null | undefined) {
  const query = useQuery<FieldLabel[]>({
    queryKey: ['fieldLabels', provider],
    queryFn: () => provider ? api.getFieldLabelsByProvider(provider) : Promise.resolve([]),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !!provider,
  })

  // Create a lookup map from field_name to FieldLabel
  const labelMap = useMemo(() => {
    const map = new Map<string, FieldLabel>()
    if (query.data) {
      for (const label of query.data) {
        map.set(label.field_name, label)
      }
    }
    return map
  }, [query.data])

  // Get display label for a field
  const getDisplayLabel = (fieldName: string): string => {
    const label = labelMap.get(fieldName)
    if (label) {
      return label.display_label
    }
    // Check for hardcoded overrides (e.g. "fläche_km2" → "Fläche")
    const override = getDisplayLabelOverride(fieldName)
    if (override) return override
    // Fallback to formatted field name
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Check if a field is visible
  const isFieldVisible = (fieldName: string): boolean => {
    const label = labelMap.get(fieldName)
    // If no label found, default to visible
    return label?.is_visible ?? true
  }

  return {
    ...query,
    labelMap,
    getDisplayLabel,
    isFieldVisible,
  }
}

// =============================================================================
// Indicators (Regionalstatistik)
// =============================================================================

import type { EntityIndicatorsResponse, AvailableIndicatorsResponse } from '../services/api'

/**
 * Get indicator values for an entity (municipality, county, state, etc.)
 * @param entityType - One of: municipality, county, state, government_district, federal
 * @param ags - AGS code (8-digit for municipality, 5-digit for county, 2-digit for state)
 * @param year - Optional: filter by exact reference year
 * @param maxYear - Optional: filter by max reference year (for edition filtering)
 */
export function useEntityIndicators(
  entityType: string | null | undefined,
  ags: string | null | undefined,
  year?: number,
  maxYear?: number
) {
  return useQuery<EntityIndicatorsResponse>({
    queryKey: queryKeys.indicators.entity(entityType!, ags!, year, maxYear),
    queryFn: () => api.getEntityIndicators(entityType!, ags!, year, maxYear),
    enabled: !!entityType && !!ags,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get available indicators for an entity type
 */
export function useAvailableIndicators(entityType: string | null | undefined) {
  return useQuery<AvailableIndicatorsResponse>({
    queryKey: queryKeys.indicators.available(entityType!),
    queryFn: () => api.getAvailableIndicators(entityType!),
    enabled: !!entityType,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}
